import React, {FC, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {Flex, useToast} from "@chakra-ui/react";
import {FiRepeat, FiTag, FiTrash} from "react-icons/all";
import {BreadcrumbsData} from "../../shared/entities/Breadcrumbs/BreadCrumbsData";
import {ModalService} from "../../service/Modal/ModalService";
import {setBreadcrumbsLinks} from "../../redux/Breadcrumbs/BreadcrumbsReducer";
import {FilterInterceptor} from "../../shared/utilities/Filters/FilterInterceptor";
import ColumnHandler, {ColumnConfig} from "../../components/Buttons/ColumnHandler";
import {FieldType} from "../../common/constants";
import {ListProductItemConfig, ListProductItemRow} from "../../components/List/ListProductItem";
import {hasPermission, isRoleAdmin} from "../../features/Permission/AllowedAccess";
import IconWithTooltip from "../../components/Icon/IconWithTooltip";
import {Breadcrumbs} from "../../components/Breadcrumbs/Breadcrumbs";
import {Loader} from "../../components/Loader/Loader";
import {Paginator} from "../../components/Paignator/Paginator";
import {ScheduledShipmentController} from "../../controllers/ScheduledShipmentController";
import {ReqSSConfig, ScheduledShipment} from "../../shared/entities/Order/ScheduledShipment";
import {
    scheduledShipmentCreatePermission, scheduledShipmentEditPermission, scheduledShipmentForcePermission,
    scheduledShipmentRemovePermission
} from "../permissions";
import {useAppSelector} from "../../redux/Store";
import AddScheduledShipmentPopover from "../Modal/AddScheduledShipmentPopover";
import ImageCustom from "../../components/Image/ImageCustom";
import {URLPaths} from "../../config/application/URLPaths";
import {useNavigate} from "react-router";

export const ScheduledShipments: FC<{ links: Array<BreadcrumbsData> }> = ({links}) => {
        const dispatch = useDispatch();
        const toast = useToast();
        const [isLoading, setIsLoading] = useState(false)
        const modalService = new ModalService(dispatch)
        const [refresh, setRefresh] = useState(false);
        const [listAll, setListAll] = useState<any>({items: [], count: 0})
        const scheduledShipmentController = new ScheduledShipmentController(dispatch)
        setBreadcrumbsLinks(dispatch, links);
        const navigate = useNavigate();

        const [currentPaginatorPosition, setCurrentPaginatorPosition] = useState(1)
        const [previewPaginatorPosition, setPreviewPaginatorPosition] = useState(1)
        const [filterInterceptor] = useState(new FilterInterceptor([]));
        const [filterConfig, setFilterConfig] = useState<any>()
        const user = useAppSelector(store => store.AuthReducer.user);
        const adminDealerships = useAppSelector(state => state.UserReducer.dealershipsMinInfo);
        const dealerships = isRoleAdmin() ? adminDealerships : user?.dealerships?.map(v => ({
            id: v.id,
            name: v.name
        })) || [];

        const handlePageChange = (newPage: number) => {
            setPreviewPaginatorPosition(currentPaginatorPosition);
            setCurrentPaginatorPosition(newPage);
        };


        useEffect(() => {
            setIsLoading(true)
            const reqConfig: ReqSSConfig = {
                params: {
                    page: (currentPaginatorPosition === previewPaginatorPosition) ? 0 : (currentPaginatorPosition > 0) ? currentPaginatorPosition - 1 : 0,
                    size: 20
                },
                data: {}
            }
            setPreviewPaginatorPosition(currentPaginatorPosition);
            filterInterceptor.getFilterMap().forEach((filter, key) => {
                if (filter && reqConfig.data) {
                    reqConfig.data[key] = filter.value;
                }
            })
            scheduledShipmentController.all(reqConfig).then(res => {
                setIsLoading(false)
                setListAll(res)
            })
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [filterConfig, currentPaginatorPosition, refresh])

        const columnsAll: Array<ColumnConfig> = [
            {
                header: "Image",
                width: "5%",
                type: FieldType.Input
            },
            {
                header: "PRODUCT",
                filterField: "product",
                isLink: false,
                filter: true,
                width: "25%",
                type: FieldType.Input
            },
            {
                header: "DEALERSHIP",
                filterField: "dealershipId",
                isLink: false,
                filter: true,
                width: "20%",
                type: FieldType.Select,
                options: [{id: "DEFAULT", name: ""}, ...dealerships]
            },
            {
                header: "QUANTITY",
                filterField: "quantity",
                isLink: false,
                width: "6%",
                type: FieldType.Input
            },
            {
                header: "PERIOD",
                filterField: "period",
                isLink: false,
                width: "6%",
                type: FieldType.Input
            },
            {
                header: "NEXT START DATE",
                filterField: "nextStartDate",
                filter: true,
                isLink: false,
                width: "11%",
                type: FieldType.DateRange
            },
            {
                header: "CREATED",
                filter: true,
                filterField: "createdAt",
                isLink: false,
                width: "10%",
                type: FieldType.DateRange
            },
            {
                header: "UPDATED",
                filterField: "updatedAt",
                filter: true,
                isLink: false,
                width: "10%",
                type: FieldType.DateRange
            },
            {
                header: "ACTIONS", filterField: "actions", isLink: false, width: "auto", type: FieldType.Input
            },
        ];

        const ColumnNamesMapping: Array<ListProductItemConfig> = columnsAll.map(
            (col: ColumnConfig, index: number) => {
                return {
                    content: col.filter ? (
                        <ColumnHandler
                            col={col}
                            filterInterceptor={filterInterceptor}
                            setFilterConfig={setFilterConfig}
                        />
                    ) : col.header,
                    width: columnsAll[index].width || "auto",
                    isLink: col.isLink || false,
                    style: col.style || {},
                };
            }
        );

        const confirmRemove = (id: number) => {
            modalService.setModalData({
                onSubmit: () => {
                    modalService.deleteModalData();
                    scheduledShipmentController.remove({id: id}).then((res) => {
                        toast({
                            position: "top",
                            title: res.None
                                ? `Failed to remove ID: ${id}`
                                : `Success removed. ID: ${id}`,
                            status: res.None ? 'error' : 'success',
                            duration: 3000,
                            isClosable: true,
                        })
                        setRefresh(prevIndex => !prevIndex);
                    })
                },
                isOpen: true,
                onOpen: () => {
                },
                onClose: () => {
                    modalService.deleteModalData();
                },
                modalTextData: "Are you sure you want to remove item?",
                context: "action"
            });
        };

        const confirmForce = (id: number) => {
            modalService.setModalData({
                onSubmit: () => {
                    modalService.deleteModalData();
                    scheduledShipmentController.force({id: id}).then((res) => {
                        toast({
                            position: "top",
                            title: res.None
                                ? `Failed to forced ID: ${id}`
                                : `Success forced ID: ${id}`,
                            status: res.None ? 'error' : 'success',
                            duration: 3000,
                            isClosable: true,
                        })
                        setRefresh(prevIndex => !prevIndex);
                    })
                },
                isOpen: true,
                onOpen: () => {
                },
                onClose: () => {
                    modalService.deleteModalData();
                },
                modalTextData: "Are you sure you want to forced item?",
                context: "action"
            });
        };

        const List = listAll?.items ? listAll.items.map((elem: ScheduledShipment, idx: number) => {
            const ItemConfig: Array<ListProductItemConfig> = [
                {
                    content: <ImageCustom elem={elem.product} sizeInPercent={100} currentSize={"small"}
                                          sizeBackend={"COMMON"}/>,
                },
                {
                    content: elem.product.name,
                },
                {
                    content: dealerships.find(v => v.id === elem.dealershipId)?.name || "Unknown",
                },
                {
                    content: elem.quantity,
                },
                {
                    content: formatReorderPeriod(elem.reorderPeriod),
                },
                {
                    content: elem.nextStartDate,
                },
                {
                    content: elem.createdAt
                },
                {
                    content: elem.updatedAt,
                },
                {
                    content: <IconWithTooltip icon={FiTag} tooltipMessage={'All Items Purchased'}
                                              onClick={() => navigate(`${URLPaths.ALL_PRODUCTS_ORDER.link}${elem.dealershipId}`)}
                    />,
                    isLink: false,
                },
                (hasPermission(scheduledShipmentCreatePermission) || hasPermission(scheduledShipmentEditPermission)) && {
                    content: <AddScheduledShipmentPopover
                        isSSTable={true}
                        isDisabledSelDealer={true}
                        onSuccess={() => setRefresh(prevIndex => !prevIndex)}
                        item={{...elem.product, quantity: elem.quantity}}
                        editItem={elem}
                        dealer={{
                            id: elem.dealershipId ?? 0,
                            name: dealerships.find(v => v.id === elem.dealershipId)?.name || "Unknown"
                        }}
                    />,
                    width: "auto"
                },
                // hasPermission(scheduledShipmentForcePermission) && {
                //     content: <IconWithTooltip icon={FiRepeat} tooltipMessage={'force scheduled shipment'}/>,
                //     clickAction: () => confirmForce(elem.id),
                //     isLink: true,
                // },

                hasPermission(scheduledShipmentRemovePermission) && {
                    content: <IconWithTooltip icon={FiTrash} tooltipMessage={'remove'}/>,
                    clickAction: () => confirmRemove(elem.id),
                    isLink: true,
                },
            ].filter(Boolean) as Array<ListProductItemConfig>;
            return <ListProductItemRow key={idx} list={ItemConfig} headers={columnsAll}
                                       style={{fontSize: "13px", minHeight: "35px", justifyContent: "flex-start",
                                           bgColor: elem.isAdmin ? "lightgreen" : 'unset'}}/>
        }) : null;


    function formatReorderPeriod(days: number): string {
        if (!days) return 'no selected';

        if (days % 30 === 0) {
            const months = days / 30;
            return months === 12
                ? '1 year'
                : `${months} month${months > 1 ? 's' : ''}`;
        }
        const weeks = Math.round(days / 7); // округляем
        return `${weeks} week${weeks > 1 ? 's' : ''}`;
    }

        return (
            <>
                <Breadcrumbs/>
                <Flex flexDir="column" height="100%">
                    <Flex flexDir="row" mb="10px" justifyContent="space-between" alignItems="end">
                        <Flex
                            flexDir="row"
                            alignItems="end"
                            width="80%"
                            fontFamily="Poppins"
                            position="relative"
                            flexWrap="wrap">
                            {filterInterceptor.getFiltersRowWithCreateButton()}
                        </Flex>
                    </Flex>
                    {
                        isLoading
                            ? <Loader/>
                            : <>
                                <ListProductItemRow list={ColumnNamesMapping} style={{
                                    fontSize: "13px",
                                    fontWeight: "700",
                                    backgroundColor: "aliceblue",
                                    justifyContent: "flex-start"
                                }}/>
                                {List}
                            </>
                    }
                </Flex>
                <Paginator limit={typeof listAll?.count === 'number' ? listAll?.count : 0}
                           current={currentPaginatorPosition}
                           swapPageTrigger={handlePageChange} step={20}/>
            </>
        )
    }
;

export default ScheduledShipments;